import Container from "@/components/Container/Container.tsx";
import { HolderForm } from "@/components/HolderForm/HolderForm.tsx";
import { UserInfoFormValues } from "@/components/HolderForm/UserInfoFormValues.ts";
import { ChevronLeft } from "@/components/Icons/utility/ChevronLeft.tsx";
import { PaymentForm } from "@/components/Payment/PaymentForm/PaymentForm.tsx";
import { ReservationSidebar } from "@/components/ReservationSidebar/ReservationSidebar.tsx";
import { ReservationSteps } from "@/components/ReservationSteps/ReservationSteps.tsx";
import { useGTMBeginCheckout } from "@/helpers/gtm/useGTMBeginCheckout.ts";
import { isInquiryPaymentType } from "@/helpers/isInquiryPaymentType.ts";
import { scrollToTop } from "@/helpers/scrollToTop.ts";
import { PaymentProviderEnum, PaymentType } from "@/helpers/types/types.ts";
import { useCartDrawerState } from "@/helpers/useCartDrawerState.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.ts";
import { useIsFormValid } from "@/pages/BookingEngine/hooks/useIsFormValid.ts";
import { ReservationHolderInformationProps } from "@/pages/ReservationHolderInformation/ReservationHolderInformationProps.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { useMemo } from "react";
import { SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const HolderAndPaymentInfo = ({
  holderFormData,
  paymentFormData,
}: ReservationHolderInformationProps) => {
  const { t } = useTranslation();
  const { handleCloseCartDrawer } = useCartDrawerState();
  const { paymentEnabled, paymentType } = useGetOrganization();
  const { setPage, setReservationHolderData, calculatedCartData, setPaymentData } = useEngineStore(
    useShallow((state) => ({
      setPage: state.setPage,
      setReservationHolderData: state.setReservationHolderData,
      calculatedCartData: state.calculatedCartData,
      setPaymentData: state.setPaymentData,
    })),
  );

  const isForcedInquiry = isInquiryPaymentType(calculatedCartData);

  const isCardGuarantee = calculatedCartData?.paymentConfig?.paymentType === PaymentType.CardGuarantee;
  const handleGTMBeginCheckout = useGTMBeginCheckout();

  const shouldShowPaymentForm = useMemo(
    () =>
      !isForcedInquiry && paymentEnabled && paymentType === PaymentProviderEnum.WsPayPer && isCardGuarantee,
    [isForcedInquiry, paymentEnabled, paymentType, isCardGuarantee],
  );

  const { isFullFormValid, isHolderFormValid } = useIsFormValid(holderFormData, paymentFormData);

  const { register, handleSubmit, errors, setValue, watch, trigger, reset } = holderFormData;
  const { handleSubmitPaymentForm, onSubmitPaymentForm, reset: resetPaymentForm } = paymentFormData;

  const onSubmitResHolderInformation: SubmitHandler<UserInfoFormValues> = (data) => {
    setReservationHolderData(data);
    setPage(AppPage.ReservationOverview);
    handleGTMBeginCheckout();
  };
  const showResetButton = false;

  const handleContinue = async () => {
    await handleSubmit(onSubmitResHolderInformation)();
    if (!isForcedInquiry && paymentEnabled && isCardGuarantee) {
      await handleSubmitPaymentForm(onSubmitPaymentForm)();
    } else {
      setPaymentData(undefined);
    }
    handleCloseCartDrawer();
    scrollToTop();
  };

  return (
    <div>
      <header className="o-header">
        <Container>
          <div className="c-be-booking-navigation">
            <div className="c-be-booking-navigation__left">
              <button
                className="c-be-link"
                onClick={() => {
                  resetPaymentForm(undefined);
                  setPaymentData(undefined);
                  setPage(AppPage.ExtraOptions);
                  scrollToTop();
                }}
              >
                <span className="c-be-link__prefix">
                  <ChevronLeft />
                </span>
                <span className="c-be-link__label">{t("backToExtraOptions") as string}</span>
              </button>
            </div>
            <div className="c-be-booking-navigation__right">
              <ReservationSteps />
            </div>
          </div>
        </Container>
      </header>
      <main className="holderWrap o-page">
        <div className="o-page__inner">
          <section className="c-holder-information-options-wrapper">
            <Container>
              <div className="holderOptions c-holder-information-options">
                <div className="c-holder-information-options__wrap">
                  <div className="c-holder-information-options__options">
                    <HolderForm
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      watch={watch}
                      trigger={trigger}
                    />
                    {showResetButton && (
                      <button
                        className="c-be-button c-be-button--secondary c-be-button--small"
                        onClick={() => reset()}
                      >
                        <span className="c-be-button__label">Reset</span>
                      </button>
                    )}
                    {shouldShowPaymentForm && (
                      <div className="c-holder-information-options__payment">
                        <PaymentForm paymentFormData={paymentFormData} holderFormData={holderFormData} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="c-holder-information-options__summary">
                  <div className="c-holder-information-options__summary-inner">
                    <ReservationSidebar
                      hideRemoveRate
                      continueDisabled={shouldShowPaymentForm ? !isFullFormValid : !isHolderFormValid}
                      onContinue={handleContinue}
                    />
                  </div>
                </div>
              </div>
            </Container>
          </section>
        </div>
      </main>
    </div>
  );
};
